import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle } from 'lucide-react';
import { dismissBanner, isBannerDismissed, restoreBanner } from '@/utils/bannerCookies';

type Props = {
  /** Unique cookie key, e.g. pos_banner_trial */
  cookieKey: string;
  /** Full banner content when expanded */
  children: React.ReactNode;
  /** Title for the compact warning button */
  restoreTitle?: string;
  className?: string;
  /** Extra classes for the compact chip */
  chipClassName?: string;
  /** Stack offset when multiple floating chips (0 = first) */
  floatIndex?: number;
};

/**
 * Wraps a banner so users can dismiss it (cookie) and reopen via a floating warning chip.
 * When dismissed, takes zero layout height so POS product area stays full height.
 */
export default function DismissibleBanner({
  cookieKey,
  children,
  restoreTitle = 'Show notice',
  className = '',
  chipClassName = '',
  floatIndex = 0,
}: Props) {
  const [dismissed, setDismissed] = useState(() => isBannerDismissed(cookieKey));

  const onDismiss = useCallback(() => {
    dismissBanner(cookieKey);
    setDismissed(true);
  }, [cookieKey]);

  const onRestore = useCallback(() => {
    restoreBanner(cookieKey);
    setDismissed(false);
  }, [cookieKey]);

  if (dismissed) {
    const chip = (
      <button
        type="button"
        onClick={onRestore}
        title={restoreTitle}
        aria-label={restoreTitle}
        style={{ top: `calc(env(safe-area-inset-top, 0px) + ${0.5 + floatIndex * 2.25}rem)` }}
        className={`fixed end-2 z-[45] inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700 border border-amber-200 shadow-md hover:bg-amber-200 active:bg-amber-300 transition-colors ${chipClassName}`}
      >
        <AlertTriangle className="w-3.5 h-3.5" />
      </button>
    );
    return typeof document !== 'undefined' ? createPortal(chip, document.body) : null;
  }

  return (
    <div className={`relative ${className}`}>
      {children}
      <button
        type="button"
        onClick={onDismiss}
        title="Dismiss"
        aria-label="Dismiss"
        className="absolute top-1.5 end-1.5 z-10 p-1.5 rounded-md text-amber-700/70 hover:text-amber-900 hover:bg-amber-100/80 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
